import React, { useState } from "react";
import { ExternalLink, ImageOff } from "lucide-react";
import { Lightbox, fileUrl } from "./MessageExtras";

// `show_catalog`: references/options as a responsive thumbnail list — picture, title, short description, optional price & link.
// Without any picture the list falls back to a compact numbered text layout instead of a grid of grey placeholders.
export function CatalogCard({ m }) {
  const [open, setOpen] = useState(null);
  const c = m.catalog;
  if (m.tool !== "catalog" || !c?.items?.length) return null;
  const visual = c.items.some((it) => it.image_url || it.image_path);
  return (
    <div className="mt-2 w-full max-w-2xl" data-testid="catalog-card">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400"><span data-no-i18n>{c.title}</span> · {c.items.length} <span>pilihan</span></p>
      {visual ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {c.items.map((it, i) => <CatalogItem key={i} it={it} i={i} onOpen={() => (it.image_url || it.image_path) && setOpen(it)} />)}
        </div>
      ) : (
        <ol className="space-y-1.5" data-testid="catalog-list">
          {c.items.map((it, i) => <CatalogRow key={i} it={it} i={i} />)}
        </ol>
      )}
      {open && <Lightbox media={{ url: open.image_url, path: open.image_path, name: open.title }} onClose={() => setOpen(null)} />}
    </div>
  );
}

function PriceLink({ it, i }) {
  return (
    <div className="mt-auto flex items-center justify-between gap-2 pt-1">
      {it.price ? <span className="text-[11px] font-semibold text-[#2F6BFF]" data-no-i18n>{it.price}</span> : <span />}
      {it.url && <a href={it.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-0.5 text-[11px] font-semibold text-slate-500 hover:text-[#2F6BFF]" data-testid={`catalog-item-link-${i + 1}`}>Lihat <ExternalLink size={11} /></a>}
    </div>
  );
}

function CatalogItem({ it, i, onOpen }) {
  const [broken, setBroken] = useState(false);
  const srcUrl = it.image_url || (it.image_path ? fileUrl(it.image_path) : null);
  const pic = srcUrl && !broken;
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`catalog-item-${i + 1}`}>
      <button type="button" onClick={onOpen} disabled={!pic} className="relative block aspect-[4/3] w-full overflow-hidden bg-slate-100" data-testid={`catalog-item-thumb-${i + 1}`}>
        {pic ? <img src={srcUrl} alt={it.title} loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={() => setBroken(true)} className="absolute inset-0 h-full w-full object-cover" />
          : <span className="absolute inset-0 flex items-center justify-center text-slate-300"><ImageOff size={22} /></span>}
        <span className="absolute left-1.5 top-1.5 rounded bg-black/60 px-1.5 text-[10px] font-bold text-white">{i + 1}</span>
        {pic && it.source === "commons" && <span className="absolute bottom-1 right-1 rounded bg-black/50 px-1 text-[9px] text-white/90" data-no-i18n>Wikimedia Commons</span>}
        {pic && it.source === "gallery" && <span className="absolute bottom-1 right-1 rounded bg-[#2F6BFF]/80 px-1 text-[9px] text-white" data-testid={`catalog-item-gallery-${i + 1}`}>Galeri kamu</span>}
      </button>
      <div className="flex flex-1 flex-col gap-0.5 px-2.5 py-2">
        <p className="line-clamp-2 text-xs font-semibold text-slate-800" data-no-i18n>{it.title}</p>
        {it.description && <p className="line-clamp-3 text-[11px] leading-snug text-slate-500" data-no-i18n>{it.description}</p>}
        <PriceLink it={it} i={i} />
      </div>
    </div>
  );
}

function CatalogRow({ it, i }) {
  return (
    <li className="flex gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2" data-testid={`catalog-item-${i + 1}`}>
      <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#EEF3FF] text-center text-[11px] font-bold leading-5 text-[#2F6BFF]">{i + 1}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-xs font-semibold text-slate-800" data-no-i18n>{it.title}</p>
        {it.description && <p className="text-[11px] leading-snug text-slate-500" data-no-i18n>{it.description}</p>}
        <PriceLink it={it} i={i} />
      </div>
    </li>
  );
}
